<!--
  @component
  The equivalent of the HTML `<input type="search">` element. It comes with a magnifier icon and
  clear button.
  @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/search
  @see https://w3c.github.io/aria/#search
-->

<script>
  import { _ } from '@sveltia/i18n';
  import Button from '../button/button.svelte';
  import Icon from '../icon/icon.svelte';
  import TextInput from './text-input.svelte';

  /**
   * @import { Snippet } from 'svelte';
   * @import { CommonEventHandlers, InputEventHandlers, TextInputProps } from '../../typedefs';
   */

  /**
   * @typedef {object} Props
   * @property {string} [value] Input value.
   * @property {Snippet} [searchIcon] Search icon slot content.
   * @property {Snippet} [closeIcon] Close icon slot content.
   * @property {() => void} [onClear] Callback invoked when the clear button is clicked.
   */

  /**
   * @type {TextInputProps & CommonEventHandlers & InputEventHandlers & Props & Record<string, any>}
   */
  let {
    /* eslint-disable prefer-const */
    value = $bindable(),
    flex = false,
    class: className,
    hidden = false,
    disabled = false,
    readonly = false,
    required = false,
    invalid = false,
    children,
    searchIcon,
    closeIcon,
    onClear,
    ...restProps
    /* eslint-enable prefer-const */
  } = $props();

  const id = $props.id();

  /**
   * Reference to the `<input>` element.
   * @type {HTMLInputElement | undefined}
   */
  let inputElement = $state();

  /**
   * Move focus to the `<input>` element.
   */
  export const focus = () => {
    inputElement?.focus();
  };
</script>

<div
  role={hidden ? undefined : 'none'}
  class="sui search-bar {className}"
  class:flex
  class:disabled
  class:readonly
  {hidden}
  aria-hidden={hidden}
>
  <span role="none">
    {#if searchIcon}
      {@render searchIcon()}
    {:else}
      <Icon name="search" />
    {/if}
  </span>
  <TextInput
    dir="auto"
    bind:element={inputElement}
    {...restProps}
    role="searchbox"
    {id}
    bind:value
    {flex}
    {hidden}
    {disabled}
    {readonly}
    {required}
    {invalid}
    inputmode="search"
  />
  {#if value}
    <Button
      iconic
      aria-label={_('_sui.clear')}
      aria-controls={id}
      onclick={() => {
        value = '';
        inputElement?.focus();
        onClear?.();
      }}
    >
      {#snippet startIcon()}
        {#if closeIcon}
          {@render closeIcon()}
        {:else}
          <Icon name="close" />
        {/if}
      {/snippet}
    </Button>
  {/if}
</div>

<style>.search-bar {
  display: inline-flex;
  align-items: center;
  position: relative;
  margin: var(--sui-focus-ring-width);
  min-width: var(--sui-textbox-singleline-min-width);
}
.search-bar.flex:not([hidden]) {
  display: inline-flex;
  width: -moz-available;
  width: -webkit-fill-available;
  width: stretch;
  min-width: 0;
}
.search-bar > span {
  position: absolute;
  inset-block: 0;
  inset-inline-start: 0;
  inset-inline-end: auto;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--sui-button-medium-height);
  height: var(--sui-button-medium-height);
}
.search-bar :global(.icon) {
  font-size: calc(var(--sui-textbox-height) * 0.6);
  opacity: 0.5;
}
.search-bar > :global(button) {
  position: absolute;
  inset-block: 0;
  inset-inline-start: auto;
  inset-inline-end: 0;
  z-index: 2;
  margin: 0 !important;
  height: var(--sui-button-medium-height);
}
.search-bar :global(.label) {
  --sui-textbox-singleline-padding: 0 36px;
}
.search-bar :global(.text-input) {
  flex: auto;
  margin: 0 !important;
  width: 0;
  min-width: 0 !important;
}
.search-bar :global(input) {
  z-index: 1;
  padding: 0 var(--sui-button-medium-height) !important;
  width: 100%;
}</style>
